import { useEffect, useState, useCallback, useRef, useMemo, type ReactNode } from 'react';
import type { TutorialDefinition } from '../types';
import { useTutorialContext } from '@/providers/TutorialProvider';
import { useTutorialEngine } from '../hooks/useTutorialEngine';
import { useAnalytics } from '@/providers/AnalyticsProvider';
import { TutorialOverlay } from '@/components/tutorial/TutorialOverlay';
import { TutorialTooltip } from '@/components/tutorial/TutorialTooltip';
import { useApiClient } from '@/providers/ApiClientProvider';
import { ui } from '@/strings';
import { useQueryClient } from '@tanstack/react-query';
import { getTutorialCompletedSuccessfully, setTutorialCompletedSuccessfully } from '../../types/window';
import { cleanupTutorialResources } from '../../cleanup';

const SETTLE_DURATION_MS = 100;
const MAX_SETTLE_WAIT_MS = 1000;
const POSITION_TOLERANCE = 0.5;
const MINIMUM_DIMENSION = 1;

interface TutorialRendererProps {
  /** Tutorial definition */
  tutorial: TutorialDefinition;

  /** Context data to pass to shouldShow function */
  context: Record<string, unknown>;

  /** Children to wrap (passes through) */
  children?: ReactNode;
}

/**
 * Tutorial Renderer
 * Integrates automation engine with existing tutorial UI components
 */
export function TutorialRenderer({ tutorial, context, children }: TutorialRendererProps) {
  const tutorialContext = useTutorialContext();
  const { track } = useAnalytics();

  const [error, setError] = useState<Error | null>(null);
  const [isExecutingAction, setIsExecutingAction] = useState(false);
  const [isSettling, setIsSettling] = useState(() => tutorialContext.activeTutorialId === tutorial.id);
  const [validationFailureCount, setValidationFailureCount] = useState(0);
  const [targetElementMissing, setTargetElementMissing] = useState(false);

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  // Track if a step transition is in progress to prevent double auto-advance
  const isTransitioningRef = useRef(false);

  // Track if we've already attempted to start this tutorial to prevent infinite loops
  const hasAttemptedStart = useRef(false);

  // Track which step we've executed actions for to prevent re-execution
  const executedActionsForStep = useRef<string | null>(null);

  // Track if state machine has been started for this tutorial session
  const hasStartedStateMachine = useRef(false);

  // Create engine
  const { stateMachine, engine } = useTutorialEngine({
    tutorial,
    onAnalytics: track,
  });
  const api = useApiClient();
  const queryClient = useQueryClient();

  const isTutorialActive = tutorialContext.activeTutorialId === tutorial.id;
  const currentStepIndex = tutorialContext.currentStepIndex;

  // Defensive: ensure step index is valid
  const safeStepIndex = Math.min(currentStepIndex, tutorial.steps.length - 1);
  const currentStep = isTutorialActive && tutorial.steps[safeStepIndex]
    ? tutorial.steps[safeStepIndex]
    : null;
  const isLastStep = safeStepIndex === tutorial.steps.length - 1;

  // Extract stable assignment ID from context to avoid re-creating cleanup function
  // Validate that the ID is a non-empty string
  const assignmentId = useMemo(() => {
    const assignment = context.assignment as { id?: string } | undefined;
    const id = assignment?.id;
    return typeof id === 'string' && id.length > 0 ? id : undefined;
  }, [context.assignment]);

  // Centralized cleanup function to ensure cleanup happens in all tutorial end scenarios
  const cleanupTutorialSession = useCallback(async () => {
    if (getTutorialCompletedSuccessfully()) {
      console.log('[TutorialRenderer] Tutorial completed successfully - deferring cleanup until modal close');
      return;
    }

    await cleanupTutorialResources(api, queryClient, {
      invalidateCaches: true,
      clearState: true,
      closeModals: false, // Don't close modals by default (for errors, completion, etc.)
    });
  }, [api, queryClient]);

  // Cleanup function specifically for skip operations (closes modals)
  const cleanupAfterSkip = useCallback(async () => {
    await cleanupTutorialResources(api, queryClient, {
      invalidateCaches: true,
      clearState: true,
      closeModals: true, // Close modals when user skips tutorial
    });
  }, [api, queryClient]);

  // Track component mount status to prevent memory leaks
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;

      // Cleanup tutorial resources if component unmounts while tutorial is active
      // Use current cleanup function directly (not through ref) to ensure fresh api/queryClient
      if (isTutorialActive) {
        cleanupTutorialSession().catch(err => {
          console.error('[TutorialRenderer] Unmount cleanup failed:', err);
        });
      }
    };
  }, [isTutorialActive, cleanupTutorialSession]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!isTutorialActive || !currentStep) {
      setIsSettling(false);
      return;
    }

    let rafId: number | null = null;
    let cancelled = false;
    let previousRect: DOMRect | null = null;
    let stableStart: number | null = null;
    let settleStart: number | null = null;

    setIsSettling(true);

    const measure = (timestamp: number) => {
      if (cancelled) {
        return;
      }

      if (settleStart === null) {
        settleStart = timestamp;
      }

      const targetElement = document.querySelector<HTMLElement>(currentStep.targetSelector);

      if (!targetElement) {
        stableStart = null;
        previousRect = null;

        if (timestamp - settleStart >= MAX_SETTLE_WAIT_MS) {
          if (isMountedRef.current) {
            setIsSettling(false);
          }
          return;
        }

        rafId = window.requestAnimationFrame(measure);
        return;
      }

      const rect = targetElement.getBoundingClientRect();

      if (!isRectVisible(rect)) {
        stableStart = null;
        previousRect = null;

        if (timestamp - settleStart >= MAX_SETTLE_WAIT_MS) {
          if (isMountedRef.current) {
            setIsSettling(false);
          }
          return;
        }

        rafId = window.requestAnimationFrame(measure);
        return;
      }

      if (previousRect && areRectsApproximatelyEqual(rect, previousRect, POSITION_TOLERANCE)) {
        if (stableStart === null) {
          stableStart = timestamp;
        }

        if (timestamp - stableStart >= SETTLE_DURATION_MS) {
          if (isMountedRef.current) {
            setIsSettling(false);
          }
          return;
        }
      } else {
        stableStart = null;
        previousRect = rect;
      }

      if (timestamp - settleStart >= MAX_SETTLE_WAIT_MS) {
        if (isMountedRef.current) {
          setIsSettling(false);
        }
        return;
      }

      rafId = window.requestAnimationFrame(measure);
    };

    rafId = window.requestAnimationFrame(measure);

    return () => {
      cancelled = true;
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      if (isMountedRef.current) {
        setIsSettling(false);
      }
    };
  }, [isTutorialActive, currentStep?.id, currentStep?.targetSelector]);

  // Extract a stable key from context to detect meaningful changes (e.g., different assignment)
  // This allows tutorial to restart when user navigates to a different assignment page
  const contextKey = useMemo(() => {
    const assignment = context.assignment as { id?: string } | undefined;
    const id = typeof assignment?.id === 'string' && assignment.id.length > 0
      ? assignment.id
      : 'none';
    return `${tutorial.id}-${id}`;
  }, [tutorial.id, context.assignment]);

  // Reset refs when context changes (e.g., navigating to different assignment)
  useEffect(() => {
    hasAttemptedStart.current = false;
    executedActionsForStep.current = null;
  }, [contextKey]);

  // Initialize state machine when tutorial becomes active
  // The TutorialInvitationBanner calls tutorialContext.startTutorial() when user clicks start.
  // We need to also start the state machine to enable step transitions.
  // Note: hasStartedStateMachine ref guards against re-execution if stateMachine reference changes
  useEffect(() => {
    if (isTutorialActive && !hasStartedStateMachine.current) {
      const state = stateMachine.getCurrentState();
      if (state.status === 'idle') {
        stateMachine.start();
        hasStartedStateMachine.current = true;
        console.log('[TutorialRenderer] State machine started for active tutorial');
      }
    }

    // Reset the flag AND clean up state machine when tutorial becomes inactive
    // This prevents memory leaks from accumulated listeners and stale action tracking
    if (!isTutorialActive && hasStartedStateMachine.current) {
      hasStartedStateMachine.current = false;
      executedActionsForStep.current = null;
      stateMachine.clearListeners();
      stateMachine.reset();
      console.log('[TutorialRenderer] State machine reset for inactive tutorial');
    }
  }, [isTutorialActive, stateMachine]);

  // Cleanup on browser close/refresh - catch hard refreshes, tab closes, browser closes
  useEffect(() => {
    if (!isTutorialActive || !assignmentId) return;

    const handleBeforeUnload = () => {
      // NOTE: Cannot perform async cleanup here - browser will close/navigate before API calls complete
      // Tutorial resources will be cleaned up on next page load (App.tsx always runs cleanup on mount)
      console.log('[TutorialRenderer] Browser closing/refreshing - tutorial session will be cleaned on next page load');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isTutorialActive, assignmentId]);

  // Execute automated actions when step starts
  useEffect(() => {
    if (!currentStep || !isTutorialActive) return;

    // Guard: prevent re-execution of actions for the same step
    if (executedActionsForStep.current === currentStep.id) {
      console.log(`[TutorialRenderer] Skipping action execution - already executed for step ${currentStep.id}`);
      return;
    }

    console.log(`[TutorialRenderer] Step ${safeStepIndex} entered:`, {
      stepId: currentStep.id,
      targetSelector: currentStep.targetSelector,
      hasActions: currentStep.onEnterActions && currentStep.onEnterActions.length > 0,
      actionCount: currentStep.onEnterActions?.length || 0,
    });

    let cancelled = false;

    const executeStepActions = async () => {
      try {
        // Execute onEnterActions if defined
        if (currentStep.onEnterActions && currentStep.onEnterActions.length > 0) {
          if (cancelled) return;
          console.log(`[TutorialRenderer] Executing ${currentStep.onEnterActions.length} actions for step ${safeStepIndex}`);
          setIsExecutingAction(true);
          await engine.executeActions(currentStep.onEnterActions);
          if (cancelled) return;
          console.log(`[TutorialRenderer] Actions completed for step ${safeStepIndex}`);
          setIsExecutingAction(false);

          // Mark this step's actions as executed
          executedActionsForStep.current = currentStep.id;
        }

        // Call lifecycle hook if defined (for custom logic)
        if (tutorial.onStart && safeStepIndex === 0) {
          if (cancelled) return;
          await tutorial.onStart();
        }
      } catch (err) {
        if (cancelled) return;
        const error = err instanceof Error ? err : new Error('Action execution failed');
        console.error(`[TutorialRenderer] Error executing actions for step ${safeStepIndex}:`, {
          error: error.message,
          stack: error.stack,
          stepId: currentStep.id,
        });
        setError(error);
        stateMachine.error(error);

        track('tutorial_error', {
          tutorialId: tutorial.id,
          stepIndex: safeStepIndex,
          error: error.message,
        });

        // Call error handler if defined
        if (tutorial.onError) {
          await tutorial.onError(error, stateMachine.getCurrentState());
        }

        // Mark tutorial as failed in provider BEFORE cleanup to prevent double cleanup
        // Only cleanup on successful skip to prevent state mismatch
        try {
          await tutorialContext.skipTutorial(false);

          // Check if component is still mounted before cleanup
          if (!isMountedRef.current) {
            console.log('[TutorialRenderer] Component unmounted during error handling, skipping cleanup');
            return;
          }

          // Cleanup only AFTER successful skip - close modals since tutorial is ending
          await cleanupAfterSkip();
        } catch (skipError) {
          console.error('[TutorialRenderer] Failed to skip tutorial after error:', skipError);
          // Don't cleanup - state remains consistent (tutorial still active, can retry)
        }
      }
    };

    void executeStepActions();

    return () => {
      cancelled = true;
      setIsExecutingAction(false);
    };
  }, [currentStep, safeStepIndex, isTutorialActive, engine, stateMachine, tutorial, track, tutorialContext, cleanupAfterSkip]);

  // Auto-advance when validation passes (if autoAdvance is true)
  useEffect(() => {
    if (!currentStep || !isTutorialActive || !currentStep.autoAdvance) return;
    if (!currentStep.validateComplete) return;
    if (isExecutingAction) return; // Wait for actions to complete first

    let cancelled = false;
    let pollInterval: NodeJS.Timeout | null = null;

    const checkValidation = async () => {
      try {
        const isValid = await currentStep.validateComplete?.();
        if (cancelled) return;

        if (isValid) {
          // CRITICAL: Check if a transition is already in progress (prevent double auto-advance)
          if (isTransitioningRef.current) {
            console.log('[TutorialRenderer] Transition already in progress, skipping auto-advance');
            return;
          }

          // Set transition lock
          isTransitioningRef.current = true;

          // Validation passed - wait a brief moment for UI to update before advancing
          await new Promise((resolve) => setTimeout(resolve, 300));
          if (cancelled) {
            isTransitioningRef.current = false;
            return;
          }

          // Auto-advance to next step
          if (isLastStep) {
            await tutorialContext.completeTutorial();
            stateMachine.complete();
            if (tutorial.onComplete) {
              await tutorial.onComplete(stateMachine.getCurrentState());
            }
            // Mark tutorial as completed successfully - cleanup will happen when markup modal closes
            setTutorialCompletedSuccessfully(true);
          } else {
            tutorialContext.nextStep();
            stateMachine.nextStep();
          }

          // Clear transition lock
          isTransitioningRef.current = false;
        } else {
          // Keep polling until validation passes
          if (!cancelled) {
            pollInterval = setTimeout(() => {
              void checkValidation();
            }, 500);
          }
        }
      } catch (err) {
        // Validation error - stop polling and cleanup
        console.error('Auto-advance validation error:', err);
        isTransitioningRef.current = false;

        // Check if mounted before cleanup
        if (isMountedRef.current) {
          await cleanupTutorialSession();
        }
      }
    };

    // Start polling for validation after actions complete
    const startDelay = setTimeout(() => {
      if (!cancelled) {
        void checkValidation();
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(startDelay);
      if (pollInterval) clearTimeout(pollInterval);
    };
  }, [currentStep, isTutorialActive, isExecutingAction, isLastStep, tutorialContext, stateMachine, tutorial, cleanupTutorialSession]);

  // Handle next step
  const handleNext = useCallback(async () => {
    if (!currentStep) return;

    // CRITICAL: Check if a transition is already in progress (prevent double advance)
    if (isTransitioningRef.current) {
      console.log('[TutorialRenderer] Transition already in progress, skipping handleNext');
      return;
    }

    // Set transition lock
    isTransitioningRef.current = true;

    try {
      // NEW: Execute onNextActions if defined (before validation)
      if (currentStep.onNextActions && currentStep.onNextActions.length > 0) {
        console.log(`[TutorialRenderer] Executing ${currentStep.onNextActions.length} onNextActions for step ${safeStepIndex}`);
        setIsExecutingAction(true);
        await engine.executeActions(currentStep.onNextActions);

        // CRITICAL: Check if component unmounted after async operation
        if (!isMountedRef.current) {
          console.log(`[TutorialRenderer] Component unmounted, aborting handleNext`);
          return;
        }

        setIsExecutingAction(false);
        console.log(`[TutorialRenderer] onNextActions completed for step ${safeStepIndex}`);
      }

      // NEW: Wait for condition if waitFor is defined
      if (currentStep.waitFor) {
        console.log(`[TutorialRenderer] Waiting for condition on step ${safeStepIndex}`);
        setIsExecutingAction(true);

        // Poll waitFor condition with timeout
        const timeout = currentStep.timeout || 30000; // 30 second default
        const pollInterval = 500;
        const maxAttempts = Math.floor(timeout / pollInterval);

        let conditionMet = false;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          // CRITICAL: Check if component unmounted before continuing poll
          if (!isMountedRef.current) {
            console.log(`[TutorialRenderer] Component unmounted during polling at step ${safeStepIndex}, aborting`);
            return;
          }

          const result = await currentStep.waitFor();
          if (result) {
            conditionMet = true;
            break;
          }

          // Check if unmounted before waiting
          if (!isMountedRef.current) {
            console.log(`[TutorialRenderer] Component unmounted before wait at step ${safeStepIndex}, aborting`);
            return;
          }

          await new Promise((resolve) => setTimeout(resolve, pollInterval));
        }

        // Final check before setting state
        if (!isMountedRef.current) {
          console.log(`[TutorialRenderer] Component unmounted after polling at step ${safeStepIndex}, aborting`);
          return;
        }

        setIsExecutingAction(false);

        if (!conditionMet) {
          throw new Error('Timeout waiting for step condition');
        }

        console.log(`[TutorialRenderer] Condition met for step ${safeStepIndex}`);
      }

      // LEGACY: Validate step if validation is defined (deprecated - use waitFor instead)
      if (currentStep.validateComplete) {
        const isValid = await currentStep.validateComplete();
        if (!isValid) {
          const newCount = validationFailureCount + 1;
          setValidationFailureCount(newCount);

          // Log detailed validation failure info
          console.error('[TutorialRenderer] Step validation failed:', {
            tutorialId: tutorial.id,
            stepIndex: safeStepIndex,
            stepId: currentStep.id,
            stepTitle: currentStep.title,
            targetSelector: currentStep.targetSelector,
            failureCount: newCount,
            validationFunction: currentStep.validateComplete.toString(),
          });

          // After 2 failures, offer to skip this step
          if (newCount >= 2) {
            setError(new Error('Step validation failed. You can try again or skip this step.'));
          } else {
            setError(new Error('Step validation failed. Please complete the step and try again.'));
          }
          return;
        }
      }

      // Clear any errors and validation failures
      setError(null);
      setValidationFailureCount(0);

      if (isLastStep) {
        await tutorialContext.completeTutorial();
        stateMachine.complete();

        // Call completion handler if defined
        if (tutorial.onComplete) {
          await tutorial.onComplete(stateMachine.getCurrentState());
        }

        // Mark tutorial as completed successfully - cleanup will happen when markup modal closes
        // We DON'T call cleanup here to keep tutorial resources visible while user explores the markup
        setTutorialCompletedSuccessfully(true);
      } else {
        tutorialContext.nextStep();
        stateMachine.nextStep();
      }

      // Clear transition lock on success
      isTransitioningRef.current = false;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Step transition failed');

      // Track error for debugging
      track('tutorial_error', {
        tutorialId: tutorial.id,
        stepIndex: safeStepIndex,
        error: error.message,
        context: 'handleNext',
      });

      setError(error);
      setIsExecutingAction(false); // Ensure we clear executing state on error
      isTransitioningRef.current = false; // Clear transition lock on error

      // Don't cleanup immediately - let user see the error UI
      // Cleanup will happen when user chooses to skip/retry
    }
  }, [currentStep, isLastStep, tutorialContext, stateMachine, tutorial, validationFailureCount, safeStepIndex, engine, track]);

  // Handle skip entire tutorial
  const handleSkip = useCallback(async (neverShowAgain: boolean) => {
    await tutorialContext.skipTutorial(neverShowAgain);
    stateMachine.skipTutorial(neverShowAgain);

    // Cleanup on skip - close modals and return to empty assignment page
    await cleanupAfterSkip();
  }, [tutorialContext, stateMachine, cleanupAfterSkip]);

  // Handle target element not found
  const handleTargetNotFound = useCallback((selector: string) => {
    if (!targetElementMissing) {
      setTargetElementMissing(true);
      setError(new Error(`The target element "${selector}" could not be found on the page. The page may not have loaded correctly, or you may have navigated away.`));
    }
  }, [targetElementMissing]);

  // Hide on mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  if (isMobile) {
    return <>{children}</>;
  }

  if (!isTutorialActive || !currentStep) {
    return <>{children}</>;
  }

  // Error boundary UI
  if (error) {
    // Log detailed error information for debugging
    console.error('═══════════════════════════════════════════════════════');
    console.error('TUTORIAL ERROR - Please copy this entire block:');
    console.error('═══════════════════════════════════════════════════════');
    console.error('Tutorial ID:', tutorial.id);
    console.error('Step Index:', safeStepIndex);
    console.error('Step ID:', currentStep?.id);
    console.error('Step Title:', currentStep?.title);
    console.error('Target Selector:', currentStep?.targetSelector);
    console.error('Error Message:', error.message);
    console.error('Error Stack:', error.stack);
    console.error('Validation Failures:', validationFailureCount);
    console.error('Target Element Missing:', targetElementMissing);
    if (currentStep?.validateComplete) {
      console.error('Validation Function:', currentStep.validateComplete.toString());
    }
    console.error('═══════════════════════════════════════════════════════');

    return (
      <>
        {children}
        <div
          className="fixed inset-0 z-[10000] bg-brand-950/80 flex items-center justify-center p-4"
          role="dialog"
          aria-labelledby="tutorial-error-title"
          aria-describedby="tutorial-error-message"
          aria-modal="true"
        >
          <div className="bg-white rounded-2xl p-6 max-w-md shadow-2xl">
            <h3 id="tutorial-error-title" className="text-xl font-bold text-semantic-error mb-2">{ui.headers.tutorialError}</h3>
            <p id="tutorial-error-message" className="text-brand-900 mb-4">{error.message}</p>
            <button
              onClick={() => void handleSkip(false)}
              className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              aria-label={ui.actions.close}
            >
              {ui.actions.close}
            </button>
          </div>
        </div>
      </>
    );
  }

  // Build list of allowed selectors - ONLY tutorial UI is allowed
  // User cannot interact with the page during tutorial except Next/Skip buttons
  const allowedSelectors = [
    '[data-tutorial-ui]', // Tutorial tooltip with Next/Skip buttons
  ];

  // Hide entire tutorial UI while actions are executing, but keep interaction blocking
  if (isExecutingAction || isSettling) {
    return (
      <>
        {children}
        {/* Invisible overlay to block all interactions during action execution */}
        <TutorialOverlay
          targetSelector="none"
          allowedSelectors={[]}
          onTargetNotFound={handleTargetNotFound}
          invisible={true}
        >
          {/* No tooltip, just blocking overlay */}
        </TutorialOverlay>
      </>
    );
  }

  return (
    <>
      {children}
      <TutorialOverlay
        targetSelector={currentStep.targetSelector}
        allowedSelectors={allowedSelectors}
        onTargetNotFound={handleTargetNotFound}
      >
        <TutorialTooltip
          step={{
            id: currentStep.id,
            title: currentStep.title,
            description: currentStep.description,
            targetSelector: currentStep.targetSelector,
            placement: currentStep.placement,
            allowInteraction: currentStep.allowInteraction,
            nextButtonLabel: currentStep.nextButtonLabel,
          }}
          stepIndex={safeStepIndex}
          totalSteps={tutorial.steps.length}
          onNext={() => { void handleNext(); }}
          onSkip={(neverShowAgain) => void handleSkip(neverShowAgain)}
          isLastStep={isLastStep}
          helperContent={null}
          isExecutingAction={isExecutingAction}
        />
      </TutorialOverlay>
    </>
  );
}

function areRectsApproximatelyEqual(a: DOMRect, b: DOMRect, tolerance: number): boolean {
  return (
    Math.abs(a.top - b.top) <= tolerance &&
    Math.abs(a.left - b.left) <= tolerance &&
    Math.abs(a.width - b.width) <= tolerance &&
    Math.abs(a.height - b.height) <= tolerance
  );
}

function isRectVisible(rect: DOMRect): boolean {
  return rect.width >= MINIMUM_DIMENSION && rect.height >= MINIMUM_DIMENSION;
}
